import { ASSISTANT_EDITOR_TOOL_NAME } from '@/reports/bundles/assistantTools';
import { INPUT_SHEET_FILE, INPUT_SHEET_TYPES_FILE, SCHEMA_REF_FILE } from '@/reports/bundles/inputSheet';
import { describeRun } from '@/reports/bundles/runOutline';
import { BUNDLE_STARTERS, starterBundleFiles } from '@/reports/bundles/starter';
import { CompiledReportBundle, REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY, ReportBundleDiagnostic, ReportBundleFiles, isValidBundlePath } from '@/reports/bundles/types';
import { InputsReportContext, RunReportContext } from '@/reports/types';
import { previewOutline } from './previewOutline';

/**
 * The assistant's tools, run in the studio: the editor tool over the files in
 * the editor, compile through the author's own Save, the preview through the
 * same compiled scripts the Preview tab runs, and the run in the preview.
 * Every tool answers in plain text, with a short label for the transcript.
 */

/** What the tools reach of the studio. Read through getters: a tool runs long after the render that made it. */
export interface AssistantWorkspace {
  getFiles: () => ReportBundleFiles;
  /** replace the working files; `changed` is the file a tool just wrote, for the editor to show */
  setFiles: (files: ReportBundleFiles, changed?: string) => void;
  /** save and compile the working files; null when the save itself failed */
  compile: () => Promise<{ compiled: CompiledReportBundle | null; diagnostics: ReportBundleDiagnostic[] } | null>;
  /** what last compiled, and the files it was compiled from */
  getCompiled: () => { compiled: CompiledReportBundle | null; files: ReportBundleFiles };
  getContext: () => RunReportContext;
  /** for a report filled in from its input sheet: the sheet as the preview fills it in, and what fills it; null for a run report */
  getInputsPreview?: () => { context: InputsReportContext; from: string } | null;
  /** is the preview on the sample run, not a real one */
  isSampleContext: () => boolean;
  loadRun: (runId: string) => Promise<string | null>;
  /** the SDK's sources, `sdk/…` */
  getSdkFiles: () => Record<string, string>;
  /** the editor's TypeScript check of the working files; null when there is no checker to ask */
  typeCheck?: () => Promise<ReportBundleDiagnostic[] | null>;
  /** the only files there are, and the only ones the editor tool may write: a schema's page has inputs.json alone */
  onlyFiles?: string[];
  /** a schema's page: save inputs.json as the schema's draft; null when the save itself failed */
  saveSchema?: () => Promise<SchemaSaveOutcome | null>;
}

/** What saving a schema's draft came to, for save_schema to say. */
export interface SchemaSaveOutcome {
  problems: string[];
  /** how many inputs the sheet holds; null when it is no sheet */
  inputs: number | null;
  /** ids renamed or removed since the latest published version, said as the studio says them */
  breaks: string[];
  latestVersion: number | null;
  readers: string[];
  /** saved elsewhere since: not saved, the author picks */
  conflict?: boolean;
}

/** How a tool call reads in the transcript. */
export interface ToolDisplay {
  verb: string;
  target?: string;
  detail?: string;
  /** an edit: the file before and after, for its diff */
  change?: { path: string; before: string; after: string };
}

export interface ToolOutcome {
  content: string;
  isError: boolean;
  display: ToolDisplay;
}

const ENTRIES = [REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY];
const SCHEMA_FILES = [INPUT_SHEET_FILE, SCHEMA_REF_FILE, INPUT_SHEET_TYPES_FILE];
const READ_LINES = 2000;

const numbered = (text: string, from = 1) => text.split('\n').map((line, i) => `${String(from + i).padStart(6)}\t${line}`).join('\n');
const failed = (content: string, display: ToolDisplay): ToolOutcome => ({ content, isError: true, display });
const done = (content: string, display: ToolDisplay): ToolOutcome => ({ content, isError: false, display });

/** `/bundle/layout.ts`, `./layout.ts`, `/layout.ts` → `layout.ts`; the root itself → ''. */
export function bundlePath(raw: unknown): string {
  return String(raw ?? '').trim().replace(/^\/+/, '').replace(/^bundle(\/|$)/, '').replace(/^(\.\/)+/, '').replace(/^\.$/, '').replace(/\/+$/, '');
}

function listing(files: ReportBundleFiles): string {
  const rank = (p: string) => (ENTRIES.includes(p) ? ENTRIES.indexOf(p) : ENTRIES.length);
  const paths = Object.keys(files).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return [`The bundle's ${paths.length} files (lines):`, ...paths.map((p) => `${p} (${files[p].split('\n').length})${ENTRIES.includes(p) ? ' — entry' : ''}`)].join('\n');
}

/** A few numbered lines around where an edit landed, so the model sees it took. */
function snippet(after: string, line: number, span: number): string {
  const lines = after.split('\n');
  const from = Math.max(1, line - 3);
  const to = Math.min(lines.length, line + span + 3);
  return numbered(lines.slice(from - 1, to).join('\n'), from);
}

function editorTool(input: Record<string, unknown>, ws: AssistantWorkspace): ToolOutcome {
  const files = ws.getFiles();
  const path = bundlePath(input.path);
  const command = String(input.command ?? '');
  const exists = path in files;

  if (command === 'view') {
    if (path === '') return done(listing(files), { verb: 'List files' });
    if (!exists) return failed(`${path} does not exist. ${listing(files)}`, { verb: 'Read', target: path });
    const lines = files[path].split('\n');
    const range = Array.isArray(input.view_range) ? (input.view_range as number[]) : null;
    const from = range ? Math.max(1, Number(range[0]) || 1) : 1;
    const to = range && Number(range[1]) !== -1 ? Math.min(lines.length, Number(range[1]) || lines.length) : Math.min(lines.length, from + READ_LINES - 1);
    const more = to < lines.length && !range ? `\n… ${lines.length - to} more lines; view_range reads them.` : '';
    return done(`${numbered(lines.slice(from - 1, to).join('\n'), from)}${more}`, { verb: 'Read', target: path, detail: range ? `lines ${from}–${to}` : undefined });
  }

  if (command === 'create') {
    if (ws.onlyFiles && !ws.onlyFiles.includes(path)) return failed(`Only ${ws.onlyFiles.join(', ')} can be written here.`, { verb: 'Write', target: path });
    if (!isValidBundlePath(path)) return failed(`"${path}" is not a bundle path: relative, plain segments, ending in .ts, .tsx or .json.`, { verb: 'Write', target: path });
    const text = String(input.file_text ?? '');
    ws.setFiles({ ...files, [path]: text }, path);
    const lineCount = text.split('\n').length;
    return done(exists ? `Overwrote ${path} (${lineCount} lines).` : `Created ${path} (${lineCount} lines).`, { verb: 'Write', target: path, change: { path, before: files[path] ?? '', after: text } });
  }

  if (command === 'str_replace') {
    if (!exists) return failed(`${path} does not exist.`, { verb: 'Edit', target: path });
    const before = files[path];
    const oldStr = String(input.old_str ?? '');
    const newStr = String(input.new_str ?? '');
    if (!oldStr) return failed('old_str is empty; give the exact text to replace.', { verb: 'Edit', target: path });
    const count = before.split(oldStr).length - 1;
    if (count === 0) return failed(`No match for old_str in ${path}. Check the exact text, whitespace included; view the file to see it as it stands.`, { verb: 'Edit', target: path });
    if (count > 1) return failed(`old_str matches ${count} places in ${path}; include more of the surrounding text to pick one.`, { verb: 'Edit', target: path });
    const at = before.indexOf(oldStr);
    const after = before.slice(0, at) + newStr + before.slice(at + oldStr.length);
    ws.setFiles({ ...files, [path]: after }, path);
    const line = before.slice(0, at).split('\n').length;
    return done(`Edited ${path}. The lines around the change now read:\n${snippet(after, line, newStr.split('\n').length)}`, { verb: 'Edit', target: path, change: { path, before, after } });
  }

  if (command === 'insert') {
    if (!exists) return failed(`${path} does not exist.`, { verb: 'Edit', target: path });
    const before = files[path];
    const lines = before.split('\n');
    const at = Number(input.insert_line);
    if (!Number.isInteger(at) || at < 0 || at > lines.length) return failed(`insert_line must be between 0 and ${lines.length}.`, { verb: 'Edit', target: path });
    const text = String(input.insert_text ?? input.new_str ?? '');
    const after = [...lines.slice(0, at), ...text.replace(/\n$/, '').split('\n'), ...lines.slice(at)].join('\n');
    ws.setFiles({ ...files, [path]: after }, path);
    return done(`Inserted after line ${at} of ${path}:\n${snippet(after, at + 1, text.split('\n').length)}`, { verb: 'Edit', target: path, change: { path, before, after } });
  }

  return failed(`Unknown command "${command}"; the editor tool takes view, create, str_replace and insert.`, { verb: 'Edit', target: path });
}

function deleteFile(input: Record<string, unknown>, ws: AssistantWorkspace): ToolOutcome {
  const files = ws.getFiles();
  const path = bundlePath(input.path);
  if (ws.onlyFiles) return failed(`${path} cannot be deleted here.`, { verb: 'Delete', target: path });
  if (ENTRIES.includes(path)) return failed(`${path} is an entry; the bundle cannot be without it.`, { verb: 'Delete', target: path });
  // the files the designer keeps a report from losing: what it reads, and the types made from it
  if (SCHEMA_FILES.includes(path)) return failed(`${path} says which schema the report reads; the bundle cannot be without it.`, { verb: 'Delete', target: path });
  if (!(path in files)) return failed(`${path} does not exist.`, { verb: 'Delete', target: path });
  const { [path]: before, ...rest } = files;
  ws.setFiles(rest);
  return done(`Deleted ${path}.`, { verb: 'Delete', target: path, change: { path, before, after: '' } });
}

const where = (d: ReportBundleDiagnostic) => `${d.file ?? '(bundle)'}${d.line ? `:${d.line}${d.column !== null ? `:${d.column + 1}` : ''}` : ''}`;

async function saveSchema(ws: AssistantWorkspace): Promise<ToolOutcome> {
  const result = ws.saveSchema ? await ws.saveSchema() : null;
  if (!result) return failed('The draft could not be saved; the page showed the author why.', { verb: 'Save', detail: 'not saved' });
  if (result.conflict) return failed('Not saved: the schema\'s draft was saved elsewhere since this page opened it. The author now picks theirs or this one; wait for them.', { verb: 'Save', detail: 'saved elsewhere' });
  if (result.problems.length) {
    return failed([`Saved; the draft is not a sheet yet (${result.problems.length} problem${result.problems.length === 1 ? '' : 's'}):`, ...result.problems.map((p) => `- ${p}`)].join('\n'), { verb: 'Save', detail: `${result.problems.length} problem${result.problems.length === 1 ? '' : 's'}` });
  }
  const lines = [`Saved: the draft is a sheet of ${result.inputs ?? 0} inputs.`];
  if (result.latestVersion !== null) {
    lines.push(result.breaks.length
      ? `Since v${result.latestVersion} it breaks ${result.breaks.length} id${result.breaks.length === 1 ? '' : 's'}, which records entered on v${result.latestVersion} and every report reading them by id stop finding:\n${result.breaks.map((b) => `- ${b}`).join('\n')}`
      : `It breaks no id since v${result.latestVersion}.`);
  } else lines.push('No version is published yet.');
  lines.push(result.readers.length ? `Read by ${result.readers.join(', ')}: each compiles against this draft on its next save.` : 'No report reads it yet.');
  return done(lines.join('\n'), { verb: 'Save', detail: result.breaks.length ? `${result.breaks.length} breaking` : 'a sheet' });
}

async function compile(ws: AssistantWorkspace): Promise<ToolOutcome> {
  const result = await ws.compile();
  if (!result) return failed('The draft could not be saved; the studio showed the author why. Nothing was compiled.', { verb: 'Compile', detail: 'not saved' });
  if (!result.compiled) {
    const problems = result.diagnostics.map((d) => `${where(d)} ${d.message}`);
    return failed([`Saved; the draft does not compile (${problems.length} problem${problems.length === 1 ? '' : 's'}):`, ...problems].join('\n'), { verb: 'Compile', detail: `${problems.length} problem${problems.length === 1 ? '' : 's'}` });
  }
  const { compiled } = result;
  const fixtures = compiled.fixtureResults ?? [];
  const failing = fixtures.filter((f) => !f.ok);
  const lines = [`Saved and compiled: "${compiled.title}" v${compiled.definitionVersion} compiles.`];
  for (const f of fixtures) {
    lines.push(f.ok
      ? `Fixture ${f.file} holds: ${f.verdict}${f.verdictLabel ? ` (${f.verdictLabel})` : ''}, ${f.findings} finding${f.findings === 1 ? '' : 's'}.`
      : `Fixture ${f.file} FAILS: ${f.error ?? 'missed its expectation'}. A failing fixture stops the bundle being published.`);
  }
  if (fixtures.length === 0) lines.push('It has no fixtures.');
  // compiling bundles without checking types; the editor's checker is what finds a type error
  const types = (await ws.typeCheck?.()) ?? null;
  if (types && types.length > 0) {
    lines.push(`The editor's type check finds ${types.length} error${types.length === 1 ? '' : 's'}; the draft runs, but fix them:`, ...types.slice(0, 40).map((d) => `${where(d)} ${d.message}`));
    if (types.length > 40) lines.push(`… ${types.length - 40} more`);
  } else if (types) {
    lines.push('The editor\'s type check finds no errors.');
  }
  const typeErrors = types?.length ? ` · ${types.length} type error${types.length === 1 ? '' : 's'}` : '';
  const detail = failing.length ? `${failing.length} fixture${failing.length === 1 ? '' : 's'} failing` : `${fixtures.length ? 'compiles · fixtures hold' : 'compiles'}${typeErrors}`;
  return { content: lines.join('\n'), isError: failing.length > 0 || !!types?.length, display: { verb: 'Compile', detail } };
}

async function preview(input: Record<string, unknown>, ws: AssistantWorkspace): Promise<ToolOutcome> {
  const { compiled, files } = ws.getCompiled();
  const section = typeof input.section === 'string' && input.section.trim() ? input.section.trim() : undefined;
  const display: ToolDisplay = { verb: 'Preview', target: section };
  if (!compiled) return failed('The draft does not compile, so there is nothing to preview; compile and fix it first.', display);
  const current = ws.getFiles();
  const changed = Object.keys({ ...current, ...files }).filter((p) => current[p] !== files[p]);
  if (changed.length) return failed(`The files changed since the last compile (${changed.join(', ')}); compile first, so the preview runs them.`, display);
  const sheet = ws.getInputsPreview?.() ?? null;
  const result = sheet
    ? await previewOutline(compiled, sheet.context, { section, on: `on its input sheet as ${sheet.from} fills it in` })
    : await previewOutline(compiled, ws.getContext(), { section, sample: ws.isSampleContext() });
  const verdict = /^Verdict: (\w+) \(prints "([^"]*)"\)/m.exec(result.text);
  return { content: result.text, isError: result.isError, display: { ...display, detail: verdict ? verdict[2] || verdict[1] : undefined } };
}

function referenceFiles(ws: AssistantWorkspace): Record<string, string> {
  const files: Record<string, string> = {};
  for (const [path, text] of Object.entries(ws.getSdkFiles())) if (path.startsWith('sdk/')) files[path] = text;
  for (const starter of BUNDLE_STARTERS) {
    for (const [path, text] of Object.entries(starterBundleFiles('example-report', 'Example report', starter))) files[`examples/${starter}-starter/${path}`] = text;
  }
  return files;
}

function readReference(input: Record<string, unknown>, ws: AssistantWorkspace): ToolOutcome {
  const files = referenceFiles(ws);
  const paths = Object.keys(files).sort();
  if (typeof input.search === 'string' && input.search.trim()) {
    let pattern: RegExp;
    try {
      pattern = new RegExp(input.search, 'i');
    } catch {
      return failed(`"${input.search}" is not a regular expression.`, { verb: 'Search SDK', target: input.search });
    }
    const hits: string[] = [];
    for (const path of paths) {
      files[path].split('\n').forEach((line, i) => { if (pattern.test(line)) hits.push(`${path}:${i + 1}: ${line.trim().slice(0, 200)}`); });
    }
    const shown = hits.slice(0, 200);
    if (hits.length > 200) shown.push(`… ${hits.length - 200} more; search for something narrower`);
    return done(hits.length ? shown.join('\n') : `Nothing matches /${input.search}/.`, { verb: 'Search SDK', target: input.search, detail: `${hits.length} match${hits.length === 1 ? '' : 'es'}` });
  }
  if (typeof input.path === 'string' && input.path.trim()) {
    const wanted = input.path.trim().replace(/^\/+/, '');
    const path = paths.find((p) => p === wanted) ?? paths.find((p) => p.endsWith(`/${wanted}`));
    if (!path) return failed(`No reference file ${wanted}. Read with no input to list them.`, { verb: 'Read SDK', target: wanted });
    const lines = files[path].split('\n');
    const from = Math.max(1, Number(input.offset) || 1);
    const to = Math.min(lines.length, from - 1 + (Number(input.limit) || READ_LINES));
    const more = to < lines.length ? `\n… ${lines.length - to} more lines; offset ${to + 1} reads on.` : '';
    return done(`${path}\n${numbered(lines.slice(from - 1, to).join('\n'), from)}${more}`, { verb: 'Read SDK', target: path });
  }
  return done([`${paths.length} reference files:`, ...paths].join('\n'), { verb: 'Read SDK', detail: 'list' });
}

export async function runAssistantTool(name: string, rawInput: unknown, ws: AssistantWorkspace): Promise<ToolOutcome> {
  const input = (rawInput && typeof rawInput === 'object' ? rawInput : {}) as Record<string, unknown>;
  try {
    switch (name) {
      case ASSISTANT_EDITOR_TOOL_NAME: return editorTool(input, ws);
      case 'delete_file': return deleteFile(input, ws);
      case 'compile': return await compile(ws);
      case 'save_schema': return await saveSchema(ws);
      case 'preview': return await preview(input, ws);
      case 'inspect_run':
      case 'load_run':
        if (ws.getInputsPreview?.()) return failed('This report is filled in from its input sheet and reads no procedure run. Its inputs are in inputs.json (view it), and what the preview fills them in with is the first fixture in fixtures/.', { verb: name === 'load_run' ? 'Load run' : 'Inspect run' });
        break;
      default:
        break;
    }
    switch (name) {
      case 'inspect_run': {
        const step = typeof input.step === 'string' ? input.step : typeof input.step === 'number' ? String(input.step) : undefined;
        const search = typeof input.search === 'string' ? input.search : undefined;
        const context = ws.getContext();
        const sample = ws.isSampleContext() ? 'The preview is on the sample run, not a real one: load_run, or ask the author for a run id, before binding to a procedure.\n\n' : '';
        return done(`${sample}${describeRun(context.run, { step, search })}`, { verb: 'Inspect run', target: step ? `step ${step}` : search ? `"${search}"` : undefined, detail: step || search ? undefined : context.run.title });
      }
      case 'load_run': {
        const runId = String(input.run_id ?? '').trim();
        if (!runId) return failed('Give the run\'s id.', { verb: 'Load run' });
        const problem = await ws.loadRun(runId);
        if (problem) return failed(problem, { verb: 'Load run', target: runId });
        const { run } = ws.getContext();
        return done(`The preview now reads run "${run.title}" (${run.steps.length} steps, ${run.status}). inspect_run describes it.`, { verb: 'Load run', target: run.title });
      }
      case 'read_reference': return readReference(input, ws);
      default: return failed(`There is no tool "${name}".`, { verb: name });
    }
  } catch (error) {
    return failed(`The tool threw: ${error instanceof Error ? error.message : String(error)}`, { verb: name });
  }
}

/** How a tool call reads before it has run, from its name and input alone. */
export function pendingDisplay(name: string, rawInput: unknown): ToolDisplay {
  const input = (rawInput && typeof rawInput === 'object' ? rawInput : {}) as Record<string, unknown>;
  const path = bundlePath(input.path);
  switch (name) {
    case ASSISTANT_EDITOR_TOOL_NAME: {
      const verb = input.command === 'view' ? (path ? 'Read' : 'List files') : input.command === 'create' ? 'Write' : 'Edit';
      return { verb, target: path || undefined };
    }
    case 'delete_file': return { verb: 'Delete', target: path };
    case 'compile': return { verb: 'Compile' };
    case 'save_schema': return { verb: 'Save' };
    case 'preview': return { verb: 'Preview', target: typeof input.section === 'string' ? input.section : undefined };
    case 'inspect_run': return { verb: 'Inspect run', target: input.step ? `step ${String(input.step)}` : typeof input.search === 'string' ? `"${input.search}"` : undefined };
    case 'load_run': return { verb: 'Load run', target: String(input.run_id ?? '') };
    case 'read_reference': return { verb: typeof input.search === 'string' ? 'Search SDK' : 'Read SDK', target: typeof input.search === 'string' ? input.search : typeof input.path === 'string' ? input.path : undefined };
    default: return { verb: name };
  }
}
